import React, { useEffect, useState } from 'react';
import { AlertCircle, FileText, RefreshCw } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const PRODUCT_HINT = (
  <>
    Saved V4 product / document mappings (Documents V4 page → Import).
    Used for queueing the import job when the schedule fires.
  </>
);

const INVENTORY_HINT = (
  <>
    Saved inventory import profiles (Inventory page → Import profiles).
    Selected mapping is reused when the schedule downloads a new file.
  </>
);

function StepHint({ stepId }) {
  if (stepId === 'product_data_sync') return <p className="ahri-sched__hint mb-2">{PRODUCT_HINT}</p>;
  if (stepId === 'inventory_sync') return <p className="ahri-sched__hint mb-2">{INVENTORY_HINT}</p>;
  return null;
}

function describeProduct(option) {
  if (!option) return '';
  const mode = option.import_mode === 'document_link'
    ? 'Document links'
    : option.import_mode === 'cpn'
      ? 'CPN'
      : 'Products';
  return `${option.name} · ${mode}`;
}

const POST_IMPORT_INDEX_OPTIONS_PRODUCT = [
  { value: 'none', label: 'None — skip search index refresh' },
  { value: 'solr', label: 'Solr — refresh Solr products index' },
  { value: 'opensearch', label: 'OpenSearch — refresh OpenSearch products index' },
  { value: 'both', label: 'Both — refresh Solr and OpenSearch' },
];

// Inventory imports always publish a per-part refresh: for Solr that includes
// the AHRI Solr publish; for OpenSearch the product docs are re-pushed.
const POST_IMPORT_INDEX_OPTIONS_INVENTORY = [
  { value: 'none', label: 'None — skip search index refresh' },
  { value: 'solr', label: 'Solr — publish product + AHRI updates to Solr' },
  { value: 'opensearch', label: 'OpenSearch — publish product updates to OpenSearch' },
  { value: 'both', label: 'Both — publish to Solr (incl. AHRI) and OpenSearch' },
];

const ALL_POST_IMPORT_INDEX_VALUES = new Set(['none', 'solr', 'opensearch', 'both']);

function normalizePostImportIndex(raw) {
  const text = String(raw || '').toLowerCase();
  return ALL_POST_IMPORT_INDEX_VALUES.has(text) ? text : 'none';
}

function MappingPicker({ step, getAuthHeaders, value, onChange, onError }) {
  const stepId = step?.id;
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectionKind, setSelectionKind] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [mode, setMode] = useState(value?.mode || 'delta');
  const [filename, setFilename] = useState(value?.file_name || '');
  const [importMode, setImportMode] = useState(value?.import_mode || '');
  const [postImportIndex, setPostImportIndex] = useState(
    normalizePostImportIndex(value?.post_import_index),
  );
  const [deltaDetectDeletes, setDeltaDetectDeletes] = useState(
    Boolean(value?.delta_detect_deletes),
  );

  useEffect(() => {
    setMode(value?.mode || 'delta');
    setFilename(value?.file_name || '');
    setImportMode(value?.import_mode || '');
    setPostImportIndex(normalizePostImportIndex(value?.post_import_index));
    setDeltaDetectDeletes(Boolean(value?.delta_detect_deletes));
  }, [
    stepId,
    value?.mode,
    value?.file_name,
    value?.import_mode,
    value?.post_import_index,
    value?.delta_detect_deletes,
  ]);

  useEffect(() => {
    if (!stepId) return undefined;
    let cancelled = false;
    setLoading(true);
    const headers = getAuthHeaders ? getAuthHeaders() : {};
    fetch(`${API_BASE_URL}/api/v1/ahri/dashboard/mapping-profiles/${stepId}`, { headers })
      .then(async (resp) => {
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data.detail || `Failed to load mappings (${resp.status})`);
        }
        if (cancelled) return;
        setOptions(Array.isArray(data.options) ? data.options : []);
        setSelectionKind(data.selection_kind || null);
      })
      .catch((err) => {
        if (!cancelled) {
          onError?.(err.message || 'Unable to load saved mappings');
          setOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [stepId, refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  if (stepId !== 'product_data_sync' && stepId !== 'inventory_sync') {
    return null;
  }

  const isProduct = stepId === 'product_data_sync';
  const selectedId = isProduct
    ? (value?.mapping_preset_id != null ? String(value.mapping_preset_id) : '')
    : (value?.import_profile_name || '');

  const handleSelect = (raw) => {
    if (isProduct) {
      const id = raw ? Number(raw) : null;
      const picked = options.find((o) => o.id === id);
      onChange({
        ...(value || {}),
        mapping_preset_id: id || undefined,
        import_mode: picked?.import_mode || importMode || 'product',
      });
      if (picked?.import_mode) setImportMode(picked.import_mode);
    } else {
      onChange({
        ...(value || {}),
        import_profile_name: raw || undefined,
      });
    }
  };

  const updateMode = (next) => {
    setMode(next);
    onChange({ ...(value || {}), mode: next });
  };

  const updateFilename = (next) => {
    setFilename(next);
    onChange({ ...(value || {}), file_name: next || undefined });
  };

  const updateImportMode = (next) => {
    setImportMode(next);
    onChange({ ...(value || {}), import_mode: next });
  };

  const updatePostImportIndex = (next) => {
    const normalized = normalizePostImportIndex(next);
    setPostImportIndex(normalized);
    onChange({ ...(value || {}), post_import_index: normalized });
  };

  const updateDeltaDetectDeletes = (next) => {
    const flag = Boolean(next);
    setDeltaDetectDeletes(flag);
    onChange({ ...(value || {}), delta_detect_deletes: flag });
  };

  return (
    <div className="ahri-sched__section">
      <div className="d-flex justify-content-between align-items-start mb-1 gap-2">
        <div className="ahri-sched__label">
          <FileText size={13} className="me-1 opacity-75" aria-hidden />
          Saved field mapping
        </div>
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          onClick={() => setRefreshTick((t) => t + 1)}
          disabled={loading}
          title="Reload saved mappings"
        >
          <RefreshCw size={12} className="me-1" />
          {loading ? 'Loading…' : 'Reload'}
        </button>
      </div>

      <StepHint stepId={stepId} />

      <select
        className="form-select form-select-sm"
        value={selectedId}
        onChange={(e) => handleSelect(e.target.value)}
        aria-label="Choose saved mapping"
        disabled={loading}
      >
        <option value="">— Select a saved mapping —</option>
        {options.map((opt) => (
          isProduct ? (
            <option key={opt.id} value={String(opt.id)}>
              {describeProduct(opt)}
            </option>
          ) : (
            <option key={opt.name} value={opt.name}>
              {opt.name}
              {opt.match_basis ? ` · ${opt.match_basis}` : ''}
            </option>
          )
        ))}
      </select>

      {!loading && options.length === 0 ? (
        <div className="ahri-source__status ahri-source__status--err mt-2">
          <AlertCircle size={14} className="me-1" />
          {isProduct
            ? 'No saved product mappings found. Create one on the Documents V4 import page.'
            : 'No saved inventory profiles. Create one on the Inventory import page.'}
        </div>
      ) : null}

      <div className="row g-2 mt-2">
        {isProduct ? (
          <div className="col-12 col-md-6">
            <label className="form-label small mb-1">Import mode</label>
            <select
              className="form-select form-select-sm"
              value={importMode || (selectedId
                ? options.find((o) => String(o.id) === selectedId)?.import_mode
                : '') || 'product'}
              onChange={(e) => updateImportMode(e.target.value)}
            >
              <option value="product">Products (catalog rows)</option>
              <option value="document_link">Document links</option>
              <option value="cpn">CPN</option>
              <option value="alt_part_number">Alt part numbers</option>
              <option value="supplier">Suppliers</option>
            </select>
          </div>
        ) : (
          <div className="col-12 col-md-6">
            <label className="form-label small mb-1">Run mode</label>
            <select
              className="form-select form-select-sm"
              value={mode}
              onChange={(e) => updateMode(e.target.value)}
            >
              <option value="delta">Delta</option>
              <option value="full">Full</option>
            </select>
          </div>
        )}
        <div className="col-12 col-md-6">
          <label className="form-label small mb-1">
            File name (optional)
            <span className="ahri-sched__hint ms-2">leave blank to auto-pick newest match</span>
          </label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={filename}
            placeholder="e.g. inventory_full.csv"
            onChange={(e) => updateFilename(e.target.value)}
          />
        </div>
        <div className="col-12">
          <label className="form-label small mb-1">
            Refresh search index after import
            <span className="ahri-sched__hint ms-2">
              runs only after the import completes successfully
            </span>
          </label>
          <select
            className="form-select form-select-sm"
            value={postImportIndex}
            onChange={(e) => updatePostImportIndex(e.target.value)}
            aria-label="Post-import search index refresh"
          >
            {(isProduct ? POST_IMPORT_INDEX_OPTIONS_PRODUCT : POST_IMPORT_INDEX_OPTIONS_INVENTORY).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {!isProduct && mode === 'delta' ? (
          <div className="col-12">
            <div className="form-check form-switch">
              <input
                id={`ahri-delta-detect-deletes-${stepId}`}
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={deltaDetectDeletes}
                onChange={(e) => updateDeltaDetectDeletes(e.target.checked)}
              />
              <label
                className="form-check-label small"
                htmlFor={`ahri-delta-detect-deletes-${stepId}`}
              >
                Delete inventory rows missing from the file (delta detect deletes)
              </label>
            </div>
            <div className="ahri-sched__hint mt-1">
              Off by default for scheduled runs. When on, the importer removes
              rows in the staged warehouses that aren't in the new file.
              Safety guards refuse to run if the staged file is suspiciously
              small or would wipe out most of an affected warehouse.
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default MappingPicker;
